import { useEffect } from "react";

type ClosePageWarningModalProps = {
  warnBeforeClosing: boolean;
  warningMessage?: string;
  onCloseDespiteWarning?: () => void;
};

const ClosePageWarningModal: React.FC<ClosePageWarningModalProps> = ({
  warnBeforeClosing,
  warningMessage,
  onCloseDespiteWarning,
}) => {
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (warnBeforeClosing) {
        event.preventDefault();
        event.returnValue = ""; // For older browsers
        return warningMessage; // This message will not be displayed in modern browsers for security reasons
      }
    };

    // Chrome stops firing `unload` for all sites at Chrome 154 (22 Sep 2026); `pagehide`
    // is its documented replacement and fires on a tab close, reload or navigation away.
    // A tab switch fires neither, so this never ends an upload the user only backgrounded.
    // It also fires when the page is parked in the back/forward cache rather than
    // destroyed; the callback runs then too, so it must leave the page in a state that is
    // safe to restore (see UploadContext's handlePageHide).
    const handlePageHide = () => {
      if (warnBeforeClosing && onCloseDespiteWarning) {
        onCloseDespiteWarning();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [warnBeforeClosing, warningMessage, onCloseDespiteWarning]);

  return null;
};

export default ClosePageWarningModal;
